import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'
import { safeLocalStorage } from '../utils/safeStorage'

type ThemeMode = 'light' | 'dark' | 'system'
type FontSize = 'small' | 'medium' | 'large'

interface ThemeContextType {
  theme: ThemeMode
  fontSize: FontSize
  compactMode: boolean
  setTheme: (theme: ThemeMode) => Promise<void>
  setFontSize: (size: FontSize) => Promise<void>
  setCompactMode: (compact: boolean) => Promise<void>
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Helper functions defined outside component to be used during initialization
// The classList toggles Tailwind's dark: variants; the data-theme attribute
// activates the [data-theme="dark"] block in design/Caderno/tokens.css so
// CSS variables (surface-*/text-*) flip to the ink palette. Both must be set
// here (not in a layout) so every route — including admin — gets the full theme.
const applyTheme = (mode: ThemeMode) => {
  if (mode === 'dark' || (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    document.documentElement.classList.add('dark')
    document.documentElement.dataset.theme = 'dark'
  } else {
    document.documentElement.classList.remove('dark')
    delete document.documentElement.dataset.theme
  }
}

const applyFontSize = (size: FontSize) => {
  document.documentElement.dataset.fontSize = size

  // Apply font size classes
  const fontSizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg'
  }

  document.body.classList.remove('text-sm', 'text-base', 'text-lg')
  document.body.classList.add(fontSizeClasses[size])
}

const applyCompactMode = (compact: boolean) => {
  if (compact) {
    document.documentElement.classList.add('compact-mode')
  } else {
    document.documentElement.classList.remove('compact-mode')
  }
}

// Initialize theme based on localStorage cache, DOM state, or system preference
export const getInitialTheme = (): ThemeMode => {
  // First check localStorage for cached theme
  const cachedTheme = safeLocalStorage.getItem('theme-cache')
  if (cachedTheme === 'dark' || cachedTheme === 'light' || cachedTheme === 'system') {
    return cachedTheme as ThemeMode
  }

  // Check if dark class is already applied to DOM
  if (document.documentElement.classList.contains('dark')) {
    return 'dark'
  }

  // Sem preferência salva, permanecer em 'system' em vez de resolver aqui
  // para 'dark'/'light'. Resolver cedo apagaria a diferença entre "escolhi
  // claro" e "nunca escolhi" — e é essa diferença que faz o efeito abaixo
  // registrar o listener de prefers-color-scheme, deixando o app acompanhar
  // o SO ao vivo. applyTheme() já resolve 'system' na hora de pintar.
  return 'system'
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile, updateProfile } = useAuth()

  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const initialTheme = getInitialTheme()
    // Apply theme immediately during initialization
    applyTheme(initialTheme)
    return initialTheme
  })
  const [fontSize, setFontSizeState] = useState<FontSize>('medium')
  const [compactMode, setCompactModeState] = useState<boolean>(false)

  // Load theme settings from profile on mount - batch DOM updates
  useEffect(() => {
    if (profile?.configuracoes?.appearance) {
      const { theme: savedTheme, fontSize: savedFontSize, compactMode: savedCompactMode } = profile.configuracoes.appearance

      // Batch all DOM mutations in a single rAF to avoid layout thrashing
      requestAnimationFrame(() => {
        if (savedTheme) {
          setThemeState(savedTheme as ThemeMode)
          applyTheme(savedTheme as ThemeMode)
          safeLocalStorage.setItem('theme-cache', savedTheme)
        }

        if (savedFontSize) {
          setFontSizeState(savedFontSize as FontSize)
          applyFontSize(savedFontSize as FontSize)
        }

        if (savedCompactMode !== undefined) {
          setCompactModeState(savedCompactMode)
          applyCompactMode(savedCompactMode)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile])

  // Listen for system theme changes if using system theme
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

      const handleChange = (e: MediaQueryListEvent) => {
        applyTheme('system')
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [theme])

  const setTheme = async (newTheme: ThemeMode) => {
    try {
      setThemeState(newTheme)
      applyTheme(newTheme)

      // Cache in localStorage for immediate access on reload
      safeLocalStorage.setItem('theme-cache', newTheme)

      if (profile) {
        // Get current configuracoes or initialize empty object
        const currentConfig = profile.configuracoes || {}

        // Update appearance settings
        const updatedConfig = {
          ...currentConfig,
          appearance: {
            ...(currentConfig.appearance || {}),
            theme: newTheme
          }
        }

        await updateProfile({
          configuracoes: updatedConfig
        })
      }
    } catch (error) {
      // Error handled silently
    }
  }

  const setFontSize = async (newSize: FontSize) => {
    try {
      setFontSizeState(newSize)
      applyFontSize(newSize)
      
      if (profile) {
        // Get current configuracoes or initialize empty object
        const currentConfig = profile.configuracoes || {}
        
        // Update appearance settings
        const updatedConfig = {
          ...currentConfig,
          appearance: {
            ...(currentConfig.appearance || {}),
            fontSize: newSize
          }
        }
        
        await updateProfile({
          configuracoes: updatedConfig
        })
      }
    } catch (error) {
      // Error handled silently
    }
  }

  const setCompactMode = async (compact: boolean) => {
    try {
      setCompactModeState(compact)
      applyCompactMode(compact)
      
      if (profile) {
        // Get current configuracoes or initialize empty object
        const currentConfig = profile.configuracoes || {}
        
        // Update appearance settings
        const updatedConfig = {
          ...currentConfig,
          appearance: {
            ...(currentConfig.appearance || {}),
            compactMode: compact
          }
        }
        
        await updateProfile({
          configuracoes: updatedConfig
        })
      }
    } catch (error) {
      // Error handled silently
    }
  }

  return (
    <ThemeContext.Provider value={{
      theme,
      fontSize,
      compactMode,
      setTheme,
      setFontSize,
      setCompactMode
    }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}